// src/components/AppComponents/Sidebar/CustomerMenu/CustomerLoadErrorDialog.tsx

import { useCallback, useEffect, useRef, useState } from 'react';
import { useUserSettingsStore } from '@/domains/user/userSettings.store';
import i18n from '@/shared/i18n/i18n';
import { cx } from '@UI/lib/utils';
import { Card } from '@UI/components/Card';
import { Button } from '@UI/components/Button/button';

interface CustomerLoadErrorDialogProps {
  retryAction: () => void;
  isLoading?: boolean;
}

const RETRY_TIMEOUT_MS = 1200;

const CustomerLoadErrorDialog: React.FC<CustomerLoadErrorDialogProps> = ({
  retryAction,
  isLoading = false,
}) => {
  const isCollapsed = useUserSettingsStore(
    (state) => state.settings.menu_collapsed,
  );
  const [isCooldown, setIsCooldown] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleRetry = useCallback(() => {
    if (isCooldown || isLoading) return;

    retryAction();

    setIsCooldown(true);
    timeoutRef.current = setTimeout(() => {
      setIsCooldown(false);
      timeoutRef.current = null;
    }, RETRY_TIMEOUT_MS);
  }, [isCooldown, isLoading, retryAction]);

  const isButtonDisabled = isLoading || isCooldown;

  return (
    <div
      className={cx(
        'fixed top-[var(--nav-height)] lg:top-0 bottom-0 right-0 left-0',
        isCollapsed
          ? 'lg:left-[var(--collapsed-sidebar-width)] lg:w-[calc(100vw-var(--collapsed-sidebar-width))]'
          : 'lg:left-[var(--expanded-sidebar-width)] lg:w-[calc(100vw-var(--expanded-sidebar-width))]',
        'h-[calc(100vh-var(--nav-height))] lg:h-screen z-[100] flex items-center justify-center',
        'bg-white/70 dark:bg-darkRevving-background/70',
      )}
    >
      <Card className="w-1/2">
        <h2 className="text-xl text-2xl font-accent mb-8 text-revving-content-strong dark:text-darkRevving-content-strong">
          {i18n.t('customerLoadError.title')}
        </h2>
        <div
          className="text-lg mb-6"
          dangerouslySetInnerHTML={{
            __html: i18n.t('customerLoadError.description'),
          }}
        />
        <div className="text-center mb-6">
          <Button
            loading={isButtonDisabled}
            variant="primary"
            onClick={handleRetry}
            disabled={isButtonDisabled}
          >
            {i18n.t('customerLoadError.retry')}
          </Button>
        </div>
        <div
          dangerouslySetInnerHTML={{
            __html: i18n.t('customerLoadError.support'),
          }}
        />
      </Card>
    </div>
  );
};

export default CustomerLoadErrorDialog;
